/**
 * Campagnes marketing — rubrique Marketing.
 *
 * L'API propose des campagnes (lieux, week-ends, fêtes, sport, sécurité…)
 * rédigées dans les trois langues ; rien ne part sans validation ici.
 * Endpoints : /api/marketing/campaigns (administrateurs).
 */

export type CampaignStatus = 'pending' | 'approved' | 'sending' | 'sent' | 'rejected';
export type CampaignLang = 'fr' | 'ar' | 'en';
export type CampaignAudience = 'all' | 'renters' | 'owners' | 'user';
export type CampaignChannel = 'push' | 'email';

export interface CampaignText {
  title: string;
  body: string;
  subject: string;
  html: string;
}

export interface CampaignStats {
  recipients?: number;
  push?: { sent: number; failed: number; skipped?: number; error?: string };
  email?: { sent: number; failed: number; skipped?: number };
  error?: string;
}

/** Relecture IA (orthographe, formulation) — campaignReview.service.js côté API. */
export interface CampaignAiIssue {
  field: 'title' | 'body' | 'subject' | 'html';
  excerpt: string;
  problem: string;
  suggestion: string;
}

export interface CampaignAiReview {
  status: 'ok' | 'issues' | 'unavailable' | 'error';
  languages: Partial<Record<CampaignLang, { ok: boolean; issues: CampaignAiIssue[]; fallback?: boolean }>>;
  model: string | null;
  message?: string;
}

export interface CampaignPerson {
  id: string;
  name: string | null;
  photo?: string | null;
  email?: string | null;
}

/** Fil d'activité (table CampaignEvent) — comme celui d'une merge request. */
export type CampaignEventKind =
  | 'created' | 'edited' | 'ai_review' | 'approved' | 'approval_revoked' | 'approval_reset'
  | 'sent' | 'send_failed' | 'rejected' | 'comment' | 'reviewers_changed';

export interface CampaignEvent {
  id_event: string;
  kind: CampaignEventKind;
  actor_id: string | null;
  actor_name: string | null;
  actor_photo: string | null;
  body: string | null;
  data: any;
  created_at: string;
}

export interface MarketingCampaign {
  id_campaign: string;
  /** Numéro court affiché « !12 ». */
  number?: number;
  /** Relecteurs : des administrateurs, et l'IA si `ai_reviewer`. */
  reviewer_ids?: string[];
  reviewers?: CampaignPerson[];
  ai_reviewer?: boolean;
  comments_count?: number;
  created_by_photo?: string | null;
  kind: string;
  template_key: string | null;
  status: CampaignStatus;
  channels: CampaignChannel[];
  audience: CampaignAudience;
  /** Audience « user » : la personne visée (message rédigé depuis Telegram par l'assistant). */
  target_user_id?: string | null;
  target_user_name?: string | null;
  target_user_email?: string | null;
  content: Record<CampaignLang, CampaignText>;
  link: string | null;
  note: string | null;
  created_by: string | null;
  created_by_name?: string | null;
  reviewed_by: string | null;
  reviewed_by_name?: string | null;
  reviewed_at: string | null;
  /** Approbation à quatre yeux (jamais l'auteur) — migration 068. */
  approved_by?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  ai_review?: CampaignAiReview | null;
  ai_reviewed_at?: string | null;
  /** La relecture rangée vaut-elle pour le texte actuel ? */
  ai_review_current?: boolean;
  sent_at: string | null;
  stats: CampaignStats | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignPatch {
  content?: Record<CampaignLang, Partial<CampaignText>>;
  channels?: CampaignChannel[];
  audience?: CampaignAudience;
  link?: string | null;
  note?: string | null;
}

const base = () => `${process.env.REACT_APP_API_URL}api/marketing/campaigns`;

const request = async <T,>(url: string, token: string, init: RequestInit = {}): Promise<T> => {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {})
    }
  });
  if (res.status === 204) return null as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Erreur ${res.status}: ${res.statusText}`);
  }
  return (data?.data ?? data) as T;
};

export const getCampaigns = (token: string, status?: CampaignStatus) =>
  request<MarketingCampaign[]>(`${base()}${status ? `?status=${status}` : ''}`, token);

/** Demande une proposition maintenant ; null quand il n'y a rien à proposer pour ce genre. */
export const proposeCampaign = (token: string, kind?: string) =>
  request<MarketingCampaign | null>(`${base()}/propose`, token, { method: 'POST', body: JSON.stringify(kind ? { kind } : {}) });

export const createCampaign = (token: string, payload: CampaignPatch & { kind?: string }) =>
  request<MarketingCampaign>(base(), token, { method: 'POST', body: JSON.stringify(payload) });

export const updateCampaign = (token: string, id: string, patch: CampaignPatch) =>
  request<MarketingCampaign>(`${base()}/${id}`, token, { method: 'PATCH', body: JSON.stringify(patch) });

/** Approuve sans envoyer — refusé à l'auteur (409 SELF_APPROVAL). */
export const approveCampaign = (token: string, id: string) =>
  request<MarketingCampaign>(`${base()}/${id}/approve`, token, { method: 'POST' });

/** Envoie une campagne approuvée (409 NOT_APPROVED sinon). */
export const sendCampaign = (token: string, id: string) =>
  request<MarketingCampaign>(`${base()}/${id}/send`, token, { method: 'POST' });

/** Relance la relecture IA ; renvoie la campagne avec `ai_review`. */
export const reviewCampaignWithAi = (token: string, id: string) =>
  request<MarketingCampaign>(`${base()}/${id}/ai-review`, token, { method: 'POST' });

export const rejectCampaign = (token: string, id: string, reason?: string) =>
  request<MarketingCampaign>(`${base()}/${id}/reject`, token, { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) });

/** Une campagne (uuid ou numéro court) avec son fil d'activité. */
export const getCampaignDetail = (token: string, ref: string | number) =>
  request<{ campaign: MarketingCampaign; events: CampaignEvent[] }>(`${base()}/${ref}`, token);

export const commentCampaign = (token: string, id: string, body: string) =>
  request<{ campaign: MarketingCampaign; events: CampaignEvent[] }>(`${base()}/${id}/comments`, token, { method: 'POST', body: JSON.stringify({ body }) });

/** Seul l'auteur du commentaire peut le modifier ou le supprimer (403 sinon). */
export const editCampaignComment = (token: string, id: string, eventId: string, body: string) =>
  request<{ campaign: MarketingCampaign; events: CampaignEvent[] }>(`${base()}/${id}/comments/${eventId}`, token, { method: 'PATCH', body: JSON.stringify({ body }) });

export const deleteCampaignComment = (token: string, id: string, eventId: string) =>
  request<{ campaign: MarketingCampaign; events: CampaignEvent[] }>(`${base()}/${id}/comments/${eventId}`, token, { method: 'DELETE' });

export const setCampaignReviewers = (token: string, id: string, reviewerIds: string[], aiReviewer: boolean) =>
  request<MarketingCampaign>(`${base()}/${id}/reviewers`, token, { method: 'PUT', body: JSON.stringify({ reviewer_ids: reviewerIds, ai_reviewer: aiReviewer }) });

export const revokeCampaignApproval = (token: string, id: string) =>
  request<MarketingCampaign>(`${base()}/${id}/revoke-approval`, token, { method: 'POST' });

/** Administrateurs, pour assigner des relecteurs. */
export const getCampaignStaff = (token: string) =>
  request<CampaignPerson[]>(`${process.env.REACT_APP_API_URL}api/marketing/staff`, token);

/** Aperçu HTML de l'e-mail tel qu'il partira. */
export const getCampaignPreviewHtml = async (token: string, id: string, lang: CampaignLang): Promise<string> => {
  const res = await fetch(`${base()}/${id}/preview?lang=${lang}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Aperçu indisponible (${res.status})`);
  return res.text();
};

/* ------------------------------------------------------------------ */
/* Idées de campagnes (campaignIdeas.service.js côté API)              */
/* ------------------------------------------------------------------ */

/** Occasion du calendrier calculé par l'API : fériés, fêtes, saisons, événements. */
export interface CampaignOccasion {
  key: string;
  kind: 'holiday' | 'religious' | 'season' | 'event';
  label: string;
  starts_on: string;
  ends_on: string;
  estimated: boolean;
  note: string | null;
}

export interface CampaignIdea {
  id_idea: string;
  occasion_key: string | null;
  occasion_label: string | null;
  occasion_date: string | null;
  send_on: string | null;
  audience: 'all' | 'renters' | 'owners';
  title: string;
  angle: string | null;
  why: string | null;
  link: string | null;
  status: 'new' | 'drafted' | 'dismissed';
  id_campaign: string | null;
  campaign_number: number | null;
  campaign_status: CampaignStatus | null;
  model: string | null;
  created_at: string;
  /** Rédaction en cours sur l'API, ou son échec. */
  drafting?: boolean;
  draft_error?: string | null;
}

/** Événement local saisi par l'équipe, ajouté au calendrier des idées. */
export interface MarketingEvent {
  id_event: string;
  title: string;
  starts_on: string;
  ends_on: string | null;
  place: string | null;
  note: string | null;
}

export interface CampaignIdeasOverview {
  ideas: CampaignIdea[];
  events: MarketingEvent[];
  calendar: CampaignOccasion[];
  generated_at: string | null;
  /** Génération en arrière-plan (null tant qu'aucune n'a été lancée depuis le démarrage de l'API). */
  generation: { running: boolean; error: string | null; started_at: string; finished_at: string | null } | null;
  configured: boolean;
  today: string;
  horizon_days: number;
}

const marketingBase = () => `${process.env.REACT_APP_API_URL}api/marketing`;

export const getCampaignIdeas = (token: string) =>
  request<CampaignIdeasOverview>(`${marketingBase()}/ideas`, token);

/** Lance de nouvelles idées (en arrière-plan) ; remplace celles encore à trier. */
export const generateCampaignIdeas = (token: string) =>
  request<CampaignIdeasOverview>(`${marketingBase()}/ideas/generate`, token, { method: 'POST' });

/** Lance la rédaction de l'idée (fr, en, ar, en arrière-plan) ; la campagne est posée en attente. */
export const draftCampaignIdea = (token: string, id: string) =>
  request<CampaignIdeasOverview>(`${marketingBase()}/ideas/${id}/draft`, token, { method: 'POST' });

export const dismissCampaignIdea = (token: string, id: string) =>
  request<{ success: boolean }>(`${marketingBase()}/ideas/${id}/dismiss`, token, { method: 'POST' });

export const createMarketingEvent = (token: string, event: { title: string; starts_on: string; ends_on?: string | null; place?: string | null; note?: string | null }) =>
  request<MarketingEvent>(`${marketingBase()}/events`, token, { method: 'POST', body: JSON.stringify(event) });

export const deleteMarketingEvent = (token: string, id: string) =>
  request<{ success: boolean }>(`${marketingBase()}/events/${id}`, token, { method: 'DELETE' });

/* ------------------------------------------------------------------ */
/* Agent de campagnes, expérimental (campaignAgent.service.js côté API) */
/* ------------------------------------------------------------------ */

export interface AgentConversationSummary {
  id_conversation: string;
  title: string | null;
  created_by: string | null;
  created_by_name: string | null;
  proposals_count: number;
  pushed_count: number;
  running: boolean;
  created_at: string;
  updated_at: string;
}

export interface AgentMessage {
  id_message: string;
  role: 'user' | 'assistant';
  content: string;
  author_id: string | null;
  author_name: string | null;
  author_photo: string | null;
  model: string | null;
  created_at: string;
}

/** Proposition de l'agent (P1, P2…) : pas encore une campagne tant qu'on ne l'a pas poussée. */
export interface AgentProposal {
  id_proposal: string;
  conversation_id: string;
  message_id: string | null;
  number: number;
  audience: 'all' | 'renters' | 'owners';
  content: Partial<Record<CampaignLang, { title: string; body: string }>>;
  link: string | null;
  note: string | null;
  id_campaign: string | null;
  campaign_number: number | null;
  campaign_status: CampaignStatus | null;
  pushed_by_name: string | null;
  pushed_at: string | null;
}

export interface AgentConversation {
  conversation: { id_conversation: string; title: string | null; created_by: string | null; created_at: string; updated_at: string };
  messages: AgentMessage[];
  proposals: AgentProposal[];
  /** L'agent prépare sa réponse (en arrière-plan sur l'API). */
  running: boolean;
  error: string | null;
}

const agentBase = () => `${marketingBase()}/agent`;

export const getAgentOverview = (token: string) =>
  request<{ configured: boolean; model: string; conversations: AgentConversationSummary[] }>(agentBase(), token);

export const getAgentConversation = (token: string, id: string) =>
  request<AgentConversation>(`${agentBase()}/conversations/${id}`, token);

/** Nouvelle conversation ; le premier message lance la réponse de l'agent. */
export const startAgentConversation = (token: string, content: string) =>
  request<AgentConversation>(`${agentBase()}/conversations`, token, { method: 'POST', body: JSON.stringify({ content }) });

export const sendAgentMessage = (token: string, id: string, content: string) =>
  request<AgentConversation>(`${agentBase()}/conversations/${id}/messages`, token, { method: 'POST', body: JSON.stringify({ content }) });

export const deleteAgentConversation = (token: string, id: string) =>
  request<{ success: boolean }>(`${agentBase()}/conversations/${id}`, token, { method: 'DELETE' });

/** Crée la campagne push en attente : relecture IA, approbation par une autre personne, puis envoi. */
export const pushAgentProposal = (token: string, id: string) =>
  request<{ proposal: AgentProposal; campaign: MarketingCampaign | null }>(`${agentBase()}/proposals/${id}/push`, token, { method: 'POST' });
